<script setup lang="ts">
import { logApiError } from '~/utils/logger'

const teamPerson1 = '/images/logo.png'

interface Monitor {
  id: string
  name: string
  role: string
  bio: string | null
  image_url: string | null
  display_order: number
}

// Composable
const { getPublicMonitors } = useMonitors()

// State
const monitors = ref<Monitor[]>([])
const loading = ref(false)

// Background colors for cards (cycles through these)
const cardColors = [
  { backgroundColor: 'rgba(105, 108, 255, 0.16)', borderColor: 'rgba(105, 108, 255, 0.16)' },
  { backgroundColor: 'rgba(3, 195, 236, 0.16)', borderColor: 'rgba(3, 195, 236, 0.16)' },
  { backgroundColor: 'rgba(255, 62, 29, 0.16)', borderColor: 'rgba(255, 62, 29, 0.16)' },
  { backgroundColor: 'rgba(113, 221, 55, 0.16)', borderColor: 'rgba(113, 221, 55, 0.16)' },
]

// Fetch active monitors ordered by display_order (public endpoint - no auth required)
async function fetchMonitors() {
  loading.value = true
  try {
    const { data } = await getPublicMonitors(100)

    monitors.value = data
  }
  catch (error) {
    logApiError('/api/monitors/public', error, { operation: 'fetchMonitors' })
  }
  finally {
    loading.value = false
  }
}

// Get color for card based on index
function getCardColor(index: number) {
  return cardColors[index % cardColors.length]
}

// Lifecycle
onMounted(() => {
  fetchMonitors()
})
</script>

<template>
  <VContainer id="monitoren">
    <div class="our-team pa-">
      <div class="headers d-flex justify-center flex-column align-center">
        <VChip
          label
          color="primary"
          size="x-large"
          class="ma-2"
          style="font-size: 1rem;font-weight: 600;text-transform: uppercase;"
        >
          Onze monitoren
        </VChip>

        <h4 class="d-flex align-center text-h4 mb-1 flex-wrap justify-center">
          ontmoet de mensen die van elke dag een beleving maken
        </h4>
      </div>

      <!-- Loading State -->
      <div
        v-if="loading"
        class="d-flex justify-center py-10"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </div>

      <!-- Monitors Grid -->
      <VRow
        v-else-if="monitors.length"
        class="monitors-grid"
      >
        <VCol
          v-for="(monitor, index) in monitors"
          :key="monitor.id"
          cols="12"
          lg="3"
          sm="6"
        >
          <VCard
            flat
            min-width="267"
            class="position-relative overflow-visible team-card mb-lg-0 mb-12"
          >
            <div
              :style="{
                maxHeight: '185px',
                minHeight: '185px',
                borderRadius: '90px 20px 0 0',
                backgroundColor: getCardColor(index)!.backgroundColor,
                border: `1px solid ${getCardColor(index)!.borderColor}`,
                borderBottom: 'none',
              }"
            >
              <VImg
                :src="monitor.image_url || teamPerson1"
                height="246"
                class="team-image"
              />
            </div>
            <VCardText
              class="text-center pa-4"
              :style="{
                border: `1px solid ${getCardColor(index)!.borderColor}`,
                borderBlockStart: 'none',
                borderRadius: '0 0 6px 6px',
                boxSizing: 'border-box',
              }"
            >
              <h5 class="text-h5">
                {{ monitor.name }}
              </h5>
              <p class="text-body-1 text-disabled mb-0">
                {{ monitor.role }}
              </p>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <!-- Read More Button -->
      <div
        v-if="monitors.length"
        class="d-flex justify-center mt-8"
      >
        <VBtn
          color="primary"
          to="/ons-team"
        >
          Lees meer
        </VBtn>
      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center py-10"
      >
        <p class="text-body-1 text-disabled">
          Geen monitoren gevonden
        </p>
      </div>
    </div>
  </VContainer>
</template>

<style lang="scss" scoped>
.team-image {
  position: absolute;
  inset-block-start: -3.4rem;
  inset-inline: 0;
}

.headers {
  margin-block-end: 7.4375rem;
}

.our-team {
  margin-block: 5.25rem;
}

@media (max-width: 1264px) {
  .our-team {
    margin-block-end: 1rem;
  }
}

.team-card {
  border-radius: 90px 20px 6px 6px;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  line-height: 36px;
}

.section-title::after {
  position: absolute;

  /* background: url("@images/front-pages/icons/section-title-icon.png") no-repeat left bottom; */
  background-size: contain;
  block-size: 100%;
  content: "";
  font-weight: 800;
  inline-size: 120%;
  inset-block-end: 12%;
  inset-inline-start: -12%;
}

.monitors-grid {
  row-gap: 5rem;
}
</style>

<style lang="scss" scoped>
.team-image {
  position: absolute;
  inset-block-start: -3.4rem;
  inset-inline: 0;
}

.headers {
  margin-block-end: 7.4375rem;
}

.our-team {
  margin-block: 5.25rem;
}

@media (max-width: 1264px) {
  .our-team {
    margin-block-end: 1rem;
  }
}

.team-card {
  border-radius: 90px 20px 6px 6px;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  line-height: 36px;
}

.section-title::after {
  position: absolute;

  /* background: url("@images/front-pages/icons/section-title-icon.png") no-repeat left bottom; */
  background-size: contain;
  block-size: 100%;
  content: "";
  font-weight: 800;
  inline-size: 120%;
  inset-block-end: 12%;
  inset-inline-start: -12%;
}
</style>
